/**
 * Money utilities — the canonical way to handle monetary values across the ERP.
 *
 * See docs/platform/MONEY.md for the full guide.
 *
 * Quick rules:
 *   - DECIMAL columns come back from Sequelize as strings. Pass them through
 *     parseMoney() to do math, then serializeMoney() to persist or
 *     toMoneyNumber() to display.
 *   - Never use +, -, *, / directly on money — JS floats silently corrupt cents.
 *   - Round only at the boundary (persist / emit / display), not mid-calculation.
 */

import Decimal from "decimal.js";

/**
 * The canonical money value type. Backed by decimal.js, so arithmetic is exact
 * up to ~10^9 significant digits — more than enough for any realistic amount.
 */
export type Money = Decimal;

/**
 * Anything we know how to coerce into a Money value.
 *   - string: e.g. "12.30", "$1,234.56", "(45.00)" (accounting-style negative)
 *   - number: a JS number (precision-lossy for large values; OK at the boundary)
 *   - Decimal: pass-through
 */
export type MoneyInput = string | number | Decimal;

/**
 * Rounding modes supported by serializeMoney / roundMoney.
 *
 * Default is ROUND_HALF_EVEN (banker's rounding) — minimizes systematic bias
 * across many lines, which is what auditors and accounting standards prefer.
 *
 * Use ROUND_HALF_UP only when matching an external system that requires it
 * (some tax authorities, some EDI partners).
 */
export type MoneyRoundingMode =
  | "ROUND_HALF_EVEN"
  | "ROUND_HALF_UP"
  | "ROUND_HALF_DOWN"
  | "ROUND_UP"
  | "ROUND_DOWN"
  | "ROUND_CEIL"
  | "ROUND_FLOOR";

const ROUNDING_MODE_MAP: Record<MoneyRoundingMode, Decimal.Rounding> = {
  ROUND_HALF_EVEN: Decimal.ROUND_HALF_EVEN,
  ROUND_HALF_UP: Decimal.ROUND_HALF_UP,
  ROUND_HALF_DOWN: Decimal.ROUND_HALF_DOWN,
  ROUND_UP: Decimal.ROUND_UP,
  ROUND_DOWN: Decimal.ROUND_DOWN,
  ROUND_CEIL: Decimal.ROUND_CEIL,
  ROUND_FLOOR: Decimal.ROUND_FLOOR,
};

const DEFAULT_DECIMALS = 2;
const DEFAULT_ROUNDING: MoneyRoundingMode = "ROUND_HALF_EVEN";

/** Decimal(0). Use as a starting accumulator or sentinel. */
export const MONEY_ZERO: Money = new Decimal(0);

/**
 * Strip the cosmetics that money strings often arrive with from CSVs / forms /
 * EDI feeds: leading/trailing whitespace, en-US currency symbols, thousands
 * separators, and accounting-style parentheses for negatives.
 *
 * Strict en-US conventions: `,` is a thousands separator and `.` is the
 * decimal point. Locale-aware input (e.g. EU `1.234,56`) must be normalized
 * by the caller before reaching this layer (typically the frontend i18n
 * stack). Malformed separator patterns like `"1,2,3"` or `"12 34.56"` are
 * rejected rather than silently coerced — better to surface the bad row
 * than let it through with a wrong value.
 *
 * Returns the raw numeric portion, or null if the input is empty/malformed.
 */
function normalizeMoneyString(input: string): string | null {
  let s = input.trim();
  if (s === "") return null;

  let negative = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    negative = true;
    s = s.slice(1, -1).trim();
  }

  // Strip a single leading or trailing currency symbol (with optional adjacent
  // whitespace). Reject anything weirder — e.g. "12$34" or "1$2.34" are
  // malformed boundary input and should surface as bad rows, not be silently
  // normalized into "1234" / "12.34".
  s = s.replace(/^([$£€¥])\s*/, "").replace(/\s*([$£€¥])$/, "");
  if (/[$£€¥]/.test(s)) return null;

  if (s === "") return null;

  // Strict en-US shape: optional sign, then either
  //   - a grouped integer with proper 3-digit groups (1,234 / 12,345,678), or
  //   - a plain integer/decimal (1234567, 12.34, 12., .5).
  // Rejects "1,2,3", "1,2345", "12 34.56", "1.234,56", etc.
  const grouped = /^[+-]?\d{1,3}(,\d{3})+(\.\d+)?$/;
  const plain = /^[+-]?(\d+\.?\d*|\.\d+)$/;
  if (!grouped.test(s) && !plain.test(s)) return null;

  s = s.replace(/,/g, "");

  if (s === "" || s === "-" || s === "+") return null;
  return negative ? `-${s}` : s;
}

/**
 * Parse a value into a Money (Decimal). Throws on invalid input.
 *
 * Use this for internal values you expect to be valid. For untrusted input
 * (CSV upload, EDI feed, user form), use tryParseMoney instead so you can
 * surface the malformed row rather than crashing the whole job.
 */
export function parseMoney(input: MoneyInput): Money {
  if (input instanceof Decimal) return input;
  if (typeof input === "number") {
    if (!Number.isFinite(input)) {
      throw new Error(`parseMoney: not a finite number: ${input}`);
    }
    return new Decimal(input);
  }
  if (typeof input === "string") {
    const normalized = normalizeMoneyString(input);
    if (normalized === null) {
      throw new Error(
        `parseMoney: empty/invalid string: ${JSON.stringify(input)}`,
      );
    }
    try {
      return new Decimal(normalized);
    } catch {
      throw new Error(
        `parseMoney: not a valid decimal: ${JSON.stringify(input)}`,
      );
    }
  }
  throw new Error(
    `parseMoney: unsupported input type ${typeof input}: ${JSON.stringify(input)}`,
  );
}

/**
 * Like parseMoney, but returns null for null/undefined/empty/invalid input
 * instead of throwing. Use at system boundaries (CSV ingest, EDI, API input).
 */
export function tryParseMoney(
  input: MoneyInput | null | undefined,
): Money | null {
  if (input === null || input === undefined) return null;
  if (input instanceof Decimal) return input;
  if (typeof input === "number") {
    return Number.isFinite(input) ? new Decimal(input) : null;
  }
  if (typeof input === "string") {
    const normalized = normalizeMoneyString(input);
    if (normalized === null) return null;
    try {
      return new Decimal(normalized);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Serialize a Money value to its canonical decimal string — the form used
 * whenever the value crosses a system boundary as text: persistence to a
 * Sequelize DECIMAL column, emission to an EDI envelope, JSON over an API
 * contract, etc.
 *
 * **Locale-agnostic by design.** Always `.` as decimal point, no thousands
 * separators, no currency symbol. Always rounds to `decimals` places (default
 * 2) using banker's rounding by default. Always emits trailing zeros, so the
 * shape is stable for diffing/equality:
 *
 *   serializeMoney(parseMoney("12.3"))    // "12.30"
 *   serializeMoney(parseMoney("12.345"))  // "12.34"  (banker's: 4 is even)
 *
 * This is the inverse of `parseMoney`. For *human display* (with `$` symbol,
 * commas, locale handling), use `formatCurrency` from `@erp/shared-utils`
 * instead — that's a different concern.
 */
export function serializeMoney(
  input: MoneyInput,
  decimals: number = DEFAULT_DECIMALS,
  rounding: MoneyRoundingMode = DEFAULT_ROUNDING,
): string {
  return parseMoney(input).toFixed(decimals, ROUNDING_MODE_MAP[rounding]);
}

/**
 * Convert a Money value to a JS number.
 *
 * Use this only when you need a number specifically (charts, analytics
 * libraries, math expecting `number`). For storage and display, prefer
 * serializeMoney / formatCurrency.
 *
 * For values within ±$90 trillion at 2 decimals this is lossless. Outside
 * that range you'll silently lose precision — but that's true of any JS
 * number, and we trust the caller knows the value's scale.
 */
export function toMoneyNumber(input: MoneyInput): number {
  return parseMoney(input).toNumber();
}

/**
 * Round a Money value to N decimal places. Returns a new Decimal — does not
 * mutate.
 *
 * Most code shouldn't need this directly; serializeMoney rounds for you
 * when persisting. Use roundMoney when you need a rounded Decimal to keep
 * doing math on (rare) — e.g. when you're computing pro-rata splits and need
 * the rounded shares to feed the residue calculation.
 */
export function roundMoney(
  input: MoneyInput,
  decimals: number = DEFAULT_DECIMALS,
  rounding: MoneyRoundingMode = DEFAULT_ROUNDING,
): Money {
  return parseMoney(input).toDecimalPlaces(
    decimals,
    ROUNDING_MODE_MAP[rounding],
  );
}

export function addMoney(a: MoneyInput, b: MoneyInput): Money {
  return parseMoney(a).plus(parseMoney(b));
}

export function subtractMoney(a: MoneyInput, b: MoneyInput): Money {
  return parseMoney(a).minus(parseMoney(b));
}

export function multiplyMoney(a: MoneyInput, b: MoneyInput): Money {
  return parseMoney(a).times(parseMoney(b));
}

/**
 * Divide a Money value by another. Returns the full-precision quotient — does
 * NOT round. Round at the boundary with serializeMoney or roundMoney.
 *
 * Throws on division by zero (rather than returning Infinity / NaN).
 */
export function divideMoney(a: MoneyInput, b: MoneyInput): Money {
  const divisor = parseMoney(b);
  if (divisor.isZero()) {
    throw new Error("divideMoney: division by zero");
  }
  return parseMoney(a).div(divisor);
}

/** Sum an array of Money values. Empty array returns MONEY_ZERO. */
export function sumMoney(values: Iterable<MoneyInput>): Money {
  let total = new Decimal(0);
  for (const v of values) total = total.plus(parseMoney(v));
  return total;
}

export function negateMoney(input: MoneyInput): Money {
  return parseMoney(input).negated();
}

export function absMoney(input: MoneyInput): Money {
  return parseMoney(input).abs();
}

/**
 * Compare two Money values. Returns -1, 0, or 1 (compatible with Array#sort).
 */
export function compareMoney(a: MoneyInput, b: MoneyInput): -1 | 0 | 1 {
  const cmp = parseMoney(a).comparedTo(parseMoney(b));
  return cmp < 0 ? -1 : cmp > 0 ? 1 : 0;
}

export function isZeroMoney(input: MoneyInput): boolean {
  return parseMoney(input).isZero();
}

export function equalsMoney(a: MoneyInput, b: MoneyInput): boolean {
  return parseMoney(a).equals(parseMoney(b));
}

/**
 * Sequelize column getter/setter for a DECIMAL money column.
 *
 * Spread the result into a `@Column({...})` to make a model field auto-convert
 * to/from Decimal:
 *
 *   @Column({
 *     type: DataType.DECIMAL(15, 2),
 *     ...moneyColumnGetSet("amount", { decimals: 2 }),
 *   })
 *   amount!: Money;
 *
 * After this, reading `model.amount` gives you a Decimal (or null), and
 * `model.update({ amount: someDecimal.times(2) })` persists the canonical
 * rounded string automatically. See docs/platform/MONEY.md for tradeoffs vs.
 * boundary-conversion.
 *
 * The getter accepts the raw string the DB returns (or a Decimal, in case
 * Sequelize ever wraps it) and returns Decimal | null. The setter accepts
 * Money | string | number | null and writes the canonical string.
 */
export function moneyColumnGetSet(
  fieldName: string,
  opts: {
    decimals?: number;
    rounding?: MoneyRoundingMode;
  } = {},
): {
  get: (this: SequelizeInstanceLike) => Money | null;
  set: (
    this: SequelizeInstanceLike,
    value: MoneyInput | null | undefined,
  ) => void;
} {
  const decimals = opts.decimals ?? DEFAULT_DECIMALS;
  const rounding = opts.rounding ?? DEFAULT_ROUNDING;
  return {
    get(this: SequelizeInstanceLike): Money | null {
      const raw = this.getDataValue(fieldName);
      if (raw === null || raw === undefined) return null;
      if (raw instanceof Decimal) return raw;
      if (typeof raw === "string" || typeof raw === "number") {
        return parseMoney(raw);
      }
      throw new Error(
        `moneyColumnGetSet[${fieldName}]: unexpected raw type ${typeof raw}`,
      );
    },
    set(
      this: SequelizeInstanceLike,
      value: MoneyInput | null | undefined,
    ): void {
      // null clears the column. undefined is a no-op so partial updates
      // (e.g. `model.update({ amount: maybeUndefinedFormField })`) don't
      // silently wipe the persisted value.
      if (value === undefined) return;
      if (value === null) {
        this.setDataValue(fieldName, null);
        return;
      }
      this.setDataValue(fieldName, serializeMoney(value, decimals, rounding));
    },
  };
}

/**
 * Minimal shape we need from a Sequelize model instance for the getter/setter
 * helper. Avoids importing sequelize into shared-utils.
 */
interface SequelizeInstanceLike {
  getDataValue(name: string): unknown;
  setDataValue(name: string, value: unknown): void;
}
